import 'react-native-gesture-handler';
import React, { useEffect, useState } from 'react';
import { AppRegistry, Platform } from 'react-native';
import { enableScreens } from 'react-native-screens';
import * as SentryReact from '@sentry/react';
import Sentry from './utils/Sentry/Sentry';
import CrashScreen from './component/CrashScreen/CrashScreen';

enableScreens();

import App from './App';

const isWebOrDesktop = Platform.OS === 'web';

const TouchEventBoundary = Platform.select({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  default: Sentry.TouchEventBoundary as any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  web: React.Fragment as any,
});

const WrappedApp: React.FC = () => {
  const [crashError, setCrashError] = useState<Error | null>(null);

  useEffect(() => {
    if (isWebOrDesktop) return;
    // ErrorUtils is a global provided by the React Native JS runtime.
    const previousHandler = ErrorUtils.getGlobalHandler();
    ErrorUtils.setGlobalHandler((error, isFatal) => {
      if (isFatal) {
        // Capture to Sentry manually then show crash screen.
        // Do NOT call previousHandler for fatal errors — RN's default
        // handler terminates the JS thread, closing the app.
        Sentry.captureException(error);
        setCrashError(error);
      } else {
        previousHandler?.(error, isFatal);
      }
    });
    return () => ErrorUtils.setGlobalHandler(previousHandler);
  }, []);

  const appContent = isWebOrDesktop ? (
    <SentryReact.ErrorBoundary
      fallback={({ error, resetError }) => (
        <CrashScreen
          error={error instanceof Error ? error : undefined}
          resetError={resetError}
        />
      )}
    >
      <App />
    </SentryReact.ErrorBoundary>
  ) : (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <CrashScreen
          error={error instanceof Error ? error : undefined}
          resetError={resetError}
        />
      )}
    >
      <App />
    </Sentry.ErrorBoundary>
  );

  return (
    <TouchEventBoundary
      {...(Platform.OS !== 'web' ? { maxComponentTreeSize: 5 } : {})}
    >
      {crashError ? <CrashScreen error={crashError} /> : appContent}
    </TouchEventBoundary>
  );
};

AppRegistry.registerComponent('OolioCDS', () => WrappedApp);

if (Platform.OS === 'web') {
  AppRegistry.runApplication('OolioCDS', {
    rootTag: document.getElementById('root'),
  });
}

export { WrappedApp as App };
